import React, {
  useRef,
  useEffect,
  useCallback,
  useState,
  useMemo,
  type ReactNode,
} from 'react';
import { ArrowDown, Sparkles } from 'lucide-react';
import {
  ChatMessage,
  ClarificationBatchCard,
  ClarificationStickyCard,
  type ClarificationAnswerControl,
} from './chat-message';
import { ChatInput } from './chat-input';
import type { ChatMessageData } from './types';

// Mirrors the backend DeliverClarificationRequest.answer max_length.
const CLARIFICATION_ANSWER_MAX_LENGTH = 8000;

type RenderUnit =
  | { kind: 'single'; message: ChatMessageData }
  | {
      kind: 'batch';
      /** Stable React ``key`` (first message's id) -- unchanged when the
       *  BE re-arms the deadline on ``CLARIFICATION_COUNTDOWN_RESUMED``. */
      key: string;
      /** Deadline-based grouping key (BE-stamped); decides batch
       *  membership only -- never used as a React key. */
      groupKey: string;
      messages: ChatMessageData[];
    };

export interface ChatProps {
  /** Array of chat messages */
  messages: ChatMessageData[];
  /** Callback when a message is submitted */
  onSendMessage: (message: string) => void;
  onInputChange?: (value: string) => void;
  /** Whether a message is being sent */
  isLoading?: boolean;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Placeholder text for the input */
  placeholder?: string;
  /** Auto-scroll to bottom when new messages arrive */
  autoScroll?: boolean;
  /** Auto-focus the input */
  autoFocus?: boolean;
  /** Focus the input when this becomes true (useful for focusing on events) */
  focusTrigger?: boolean;
  /** Custom empty state content */
  emptyState?: ReactNode;
  /** Content to render above the input field */
  aboveInput?: ReactNode;
  /** Callback when user clicks "Apply changes" on a node_update message */
  onApplyUpdates?: (messageId: string) => void;
  /** Callback when user clicks "Apply changes" on a workflow_mutations_confirm message */
  onApplyWorkflowMutations?: (messageId: string) => void;
  /** Callback when user confirms removal of a node via chat */
  onConfirmRemoveNode?: (messageId: string) => void;
  /** Content rendered on the left side of the input footer (e.g. model selector) */
  footerLeft?: ReactNode;
  /**
   * Disable Apply / Confirm on every message (locked workflows): blocks
   * both newly-streamed proposals and stale pre-lock ones.
   */
  applyDisabled?: boolean;
  /** Tooltip explaining why Apply is disabled (shown on hover). */
  applyDisabledReason?: string;
  clarificationAnswerControl?: ClarificationAnswerControl;
  onFocusNode?: (nodeId: string) => void;
  // Resolves the node's live canvas label; the BE snapshot misses renames.
  resolveNodeName?: (nodeId: string) => string | undefined;
  resolveNodeIcon?: (nodeId: string) => React.ReactNode | undefined;
  onClarificationCardHoverChange?: (hovering: boolean) => void;
  /**
   * Open an artifact in the embedded right-side viewer.
   * When provided, artifact cards in chat call this instead of navigating.
   */
  onOpenArtifact?: (
    artifact: import('@/types/artifacts').ArtifactListItem,
  ) => void;
  /**
   * When provided, submits made while `isLoading` queue instead of being
   * refused (see ChatInput.onQueueMessage). Suppressed while a
   * clarification card owns the composer -- its submissions are answers.
   */
  onQueueMessage?: (message: string) => void;
  /** Passed to ChatInput so the queue strip can pop text into the composer. */
  registerComposerDraftSetter?: (
    setter: (text: string) => void,
  ) => (() => void) | void;
}

const SCROLL_BOTTOM_THRESHOLD = 60;
const SCROLL_BUTTON_THRESHOLD = 100;

export const Chat: React.FC<ChatProps> = ({
  messages,
  onSendMessage,
  onInputChange,
  isLoading = false,
  disabled = false,
  placeholder = 'Type something ...',
  autoScroll = true,
  autoFocus = false,
  focusTrigger = false,
  emptyState,
  aboveInput,
  onApplyUpdates,
  onApplyWorkflowMutations,
  onConfirmRemoveNode,
  footerLeft,
  applyDisabled = false,
  applyDisabledReason,
  clarificationAnswerControl,
  onFocusNode,
  resolveNodeName,
  resolveNodeIcon,
  onClarificationCardHoverChange,
  onOpenArtifact,
  onQueueMessage,
  registerComposerDraftSetter,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isUserScrolledUpRef = useRef(false);
  const hasInitiallyScrolledRef = useRef(false);
  const [showScrollButton, setShowScrollButton] = useState(false);

  // Same DAG level → same deadlineEpochMs (a BE invariant), so that is the
  // grouping key. Legacy IDB messages without it fall back to a per-message
  // key so they never co-group.
  const renderUnits = useMemo<RenderUnit[]>(() => {
    const units: RenderUnit[] = [];
    for (const m of messages) {
      if (m.variant === 'clarification_request' && m.clarificationRequest) {
        // Sticky-redesign: pending clarifications hoist to
        // ``ClarificationStickyCard`` above the input; only RESOLVED
        // ones stay in the transcript as historical Q&A.
        if (m.clarificationResolution === undefined) continue;
        const c = m.clarificationRequest;
        // Grouping key: ``deadlineEpochMs`` when available, else a
        // per-message key for legacy IDB cards.
        const groupKey =
          c.deadlineEpochMs != null
            ? `d:${c.deadlineEpochMs}`
            : `r:${c.requestedAt}:${m.id}`;
        const last = units[units.length - 1];
        if (last && last.kind === 'batch' && last.groupKey === groupKey) {
          last.messages.push(m);
          continue;
        }
        // React key: first message's id, NOT the deadline -- a
        // deadline-derived key would unmount/remount the card on every
        // ``CLARIFICATION_COUNTDOWN_RESUMED`` re-arm.
        units.push({
          kind: 'batch',
          key: `b:${m.id}`,
          groupKey,
          messages: [m],
        });
        continue;
      }
      units.push({ kind: 'single', message: m });
    }
    return units;
  }, [messages]);

  // Sticky-redesign: flat list of currently-pending clarifications,
  // rendered in ``ClarificationStickyCard`` above the input and
  // paginated one at a time.
  const pendingClarificationMessages = useMemo(
    () =>
      messages.filter(
        (m) =>
          m.variant === 'clarification_request' &&
          !!m.clarificationRequest &&
          m.clarificationResolution === undefined,
      ),
    [messages],
  );

  // Pagination index lives here (not in the card) because the chat
  // input below needs to know which pending question its submissions
  // answer.  Clamping handles list-shrink as questions resolve.
  const [pendingCurrentIndex, setPendingCurrentIndex] = useState(0);
  const totalPending = pendingClarificationMessages.length;
  const safePendingIndex =
    totalPending === 0 ? 0 : Math.min(pendingCurrentIndex, totalPending - 1);
  useEffect(() => {
    if (safePendingIndex !== pendingCurrentIndex) {
      setPendingCurrentIndex(safePendingIndex);
    }
  }, [safePendingIndex, pendingCurrentIndex]);
  const currentPending =
    totalPending > 0 ? pendingClarificationMessages[safePendingIndex] : null;
  const currentPendingClarification = currentPending?.clarificationRequest;
  const currentPendingNodeId = currentPendingClarification?.nodeId;

  // Answered questions stay pending as revisable drafts until the batch
  // completes, so the paginator has to advance manually. answeredNodeId is
  // excluded because its draft flag isn't committed to messages yet.
  const advanceToNextUnanswered = useCallback(
    (answeredNodeId: string) => {
      const nextIdx = pendingClarificationMessages.findIndex(
        (m) =>
          !!m.clarificationRequest &&
          m.clarificationRequest.nodeId !== answeredNodeId &&
          !m.clarificationRequest.submitted,
      );
      if (nextIdx >= 0) setPendingCurrentIndex(nextIdx);
    },
    [pendingClarificationMessages],
  );

  // Skip = "don't answer, just proceed": delivers ``declined=true``
  // (empty answer), resolved by the BE with the same no-answer
  // semantics as a timeout.  Still a revisable draft until the batch
  // commits.
  const handleSkipCurrent = useCallback(() => {
    if (!currentPendingNodeId || !clarificationAnswerControl) return;
    const skippedNodeId = currentPendingNodeId;
    void clarificationAnswerControl.onDeliverAnswer(skippedNodeId, '', true);
    advanceToNextUnanswered(skippedNodeId);
  }, [
    currentPendingNodeId,
    clarificationAnswerControl,
    advanceToNextUnanswered,
  ]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    isUserScrolledUpRef.current = false;
    setShowScrollButton(false);
  }, []);

  const handleSendMessage = useCallback(
    (message: string) => {
      // Sticky-redesign: when a clarification card is visible, the
      // chat input IS the answer field -- route the submission through
      // ``onDeliverAnswer`` instead of as a normal chat message.
      if (currentPendingNodeId && clarificationAnswerControl) {
        const answeredNodeId = currentPendingNodeId;
        void clarificationAnswerControl.onDeliverAnswer(
          answeredNodeId,
          message,
        );
        // Drafted answers stay in the pending list (revisable), so
        // step the paginator to the next unanswered question.
        advanceToNextUnanswered(answeredNodeId);
        return;
      }
      isUserScrolledUpRef.current = false;
      setShowScrollButton(false);
      onSendMessage(message);
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 50);
    },
    [
      onSendMessage,
      currentPendingNodeId,
      clarificationAnswerControl,
      advanceToNextUnanswered,
    ],
  );

  // Retitle the chat-input placeholder while a clarification is active
  // so the user knows the input answers it; a drafted question hints
  // that typing will REPLACE the prior answer.
  const effectivePlaceholder = currentPendingClarification
    ? currentPendingClarification.submitted
      ? 'Edit your answer…'
      : 'Type your answer…'
    : placeholder;

  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distFromBottom < SCROLL_BOTTOM_THRESHOLD) {
      isUserScrolledUpRef.current = false;
      setShowScrollButton(false);
    } else {
      isUserScrolledUpRef.current = true;
      setShowScrollButton(distFromBottom > SCROLL_BUTTON_THRESHOLD);
    }
  }, []);

  // Initial scroll: jump instantly to bottom on first load, then use
  // a second pass after content has had time to render fully.
  useEffect(() => {
    if (hasInitiallyScrolledRef.current || messages.length === 0) return;
    hasInitiallyScrolledRef.current = true;

    messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
    const secondPass = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
    }, 150);
    return () => clearTimeout(secondPass);
  }, [messages]);

  // Auto-scroll on new messages (after initial load)
  useEffect(() => {
    if (!autoScroll || messages.length === 0) return;
    if (isUserScrolledUpRef.current) return;
    if (!hasInitiallyScrolledRef.current) return;

    const delayedScroll = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 10);

    return () => clearTimeout(delayedScroll);
  }, [messages, autoScroll]);

  // A resolve mutates an existing message rather than appending one, and the
  // card's layout settles late, so the auto-scroll above can miss it.
  const resolvedClarificationCount = useMemo(
    () =>
      messages.filter(
        (m) =>
          m.variant === 'clarification_request' &&
          m.clarificationResolution !== undefined,
      ).length,
    [messages],
  );
  const prevResolvedClarificationCountRef = useRef(resolvedClarificationCount);
  useEffect(() => {
    const grew =
      resolvedClarificationCount > prevResolvedClarificationCountRef.current;
    prevResolvedClarificationCountRef.current = resolvedClarificationCount;
    if (!grew) return;
    if (!autoScroll) return;
    if (isUserScrolledUpRef.current) return;
    if (!hasInitiallyScrolledRef.current) return;

    const raf = requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    });
    const secondPass = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 150);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(secondPass);
    };
  }, [resolvedClarificationCount, autoScroll]);

  const renderEmptyState = () => {
    if (emptyState) {
      return emptyState;
    }

    return (
      <div className='flex h-full flex-col items-center justify-center text-center text-sm text-zinc-500 dark:text-zinc-400'>
        <div className='mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-md dark:bg-zinc-900'>
          <Sparkles className='h-5 w-5 text-primary' />
        </div>
        <p className='font-medium text-zinc-900 dark:text-zinc-100'>
          Start a conversation
        </p>
        <p className='mt-1 max-w-sm'>Ask a question or start a discussion.</p>
      </div>
    );
  };

  return (
    <div className='flex h-full flex-col bg-white dark:bg-zinc-950'>
      {/* Messages Area */}
      <div className='relative min-h-0 flex-1'>
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          tabIndex={0}
          onKeyDown={(e: React.KeyboardEvent) => e.stopPropagation()}
          className={`flex h-full flex-col gap-4 overflow-y-auto px-4 py-4 outline-none scrollbar-none [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] ${
            messages.length === 0 ? 'justify-center' : ''
          }`}
        >
          {messages.length === 0
            ? renderEmptyState()
            : renderUnits.map((unit) =>
                unit.kind === 'batch' ? (
                  <ClarificationBatchCard
                    key={unit.key}
                    messages={unit.messages}
                    resolveNodeName={resolveNodeName}
                    resolveNodeIcon={resolveNodeIcon}
                    onFocusNode={onFocusNode}
                  />
                ) : (
                  <ChatMessage
                    key={unit.message.id}
                    message={unit.message}
                    onApplyUpdates={onApplyUpdates}
                    onApplyWorkflowMutations={onApplyWorkflowMutations}
                    onConfirmRemoveNode={onConfirmRemoveNode}
                    applyDisabled={applyDisabled}
                    applyDisabledReason={applyDisabledReason}
                    onOpenArtifact={onOpenArtifact}
                  />
                ),
              )}
          <div ref={messagesEndRef} />
        </div>

        {showScrollButton && (
          <button
            onClick={scrollToBottom}
            className='absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1 rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-xs text-zinc-600 shadow-md transition-all hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700'
          >
            <ArrowDown className='h-3 w-3' />
            Scroll to bottom
          </button>
        )}
      </div>

      {/* Sticky-redesign: the active clarification card sits directly
          above the chat input, inset more (``px-8`` vs ``px-4``) so it
          reads as "popping up out of" the input.  Hover-pause handlers
          live on the card; the hook gates them on
          ``isClarificationActive``. */}
      {totalPending > 0 ? (
        <div className='px-8'>
          <ClarificationStickyCard
            messages={pendingClarificationMessages}
            currentIndex={safePendingIndex}
            onCurrentIndexChange={setPendingCurrentIndex}
            onSkipCurrent={handleSkipCurrent}
            onFocusNode={onFocusNode}
            resolveNodeName={resolveNodeName}
            resolveNodeIcon={resolveNodeIcon}
            onHoverChange={onClarificationCardHoverChange}
          />
        </div>
      ) : null}

      {/* Content above input (e.g., status bar) */}
      {aboveInput}

      {/* Input Area.  ChatInput is always at this tree position
          so toggling the sticky card above doesn't unmount it
          (preserves any typed-but-not-yet-sent text). */}
      <div className='px-4 pb-4 pt-0'>
        <ChatInput
          onSubmit={handleSendMessage}
          onInputChange={onInputChange}
          placeholder={effectivePlaceholder}
          disabled={disabled}
          isLoading={isLoading}
          autoFocus={autoFocus}
          focusTrigger={focusTrigger}
          footerLeft={footerLeft}
          // While a clarification card is up the input IS the answer field
          // (see handleSendMessage) -- submissions must route there, never
          // into the queue.
          onQueueMessage={
            currentPendingClarification ? undefined : onQueueMessage
          }
          registerDraftSetter={registerComposerDraftSetter}
          accentTone={currentPendingClarification ? 'amber' : 'default'}
          maxLength={
            currentPendingClarification
              ? CLARIFICATION_ANSWER_MAX_LENGTH
              : undefined
          }
        />
      </div>
    </div>
  );
};

export default Chat;
